import React from "react";
import {
    Hash,
    CreditCard,
    Building2,
    FileText,
    Phone,
    User,
    CalendarDays,
    CalendarClock,
    CheckCircle2,
    XCircle,
    Wallet,
    Banknote,
    ListOrdered,
    Paperclip,
} from "lucide-react";
import ModalCloseButton from "../common/ModalCloseButton";


const formatKeyAsLabel = (key) => {
    if (!key) return '';
    const result = key.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1).trim();
};

// Same detail-tile recipe used in ViewInstallment.jsx's "Installment Plan
// Details" grid, kept local to this file per this codebase's precedent of
// duplicating small presentational helpers per file. No `theme` prop is
// passed down from ViewInstallment.jsx, so this follows the file's existing
// convention of Tailwind `dark:` variants rather than the app's theme-context
// ternary pattern.
const detailCardClass = (wide) =>
    `flex items-start gap-2.5 rounded-[10px] border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-700/40 ${wide ? "sm:col-span-2 lg:col-span-3" : ""}`;

const detailIconChipClass =
    "flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] border border-gray-200 bg-white text-[#236DB4] dark:border-gray-600 dark:bg-gray-600/70 dark:text-gray-300";

const detailLabelClass =
    "mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400";

const detailValueClass =
    "text-sm font-semibold break-words text-gray-800 dark:text-gray-100";

const tableCardClass =
    "flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden dark:border-gray-700 dark:bg-gray-800/50";

const tableHeadClass =
    "sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-slate-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200";

const tableRowClass =
    "border-b border-slate-100 hover:bg-slate-50/70 dark:border-gray-700 dark:hover:bg-gray-700/50";

const thClass = "px-4 py-3 text-left rtl:text-right font-semibold text-xs uppercase tracking-[0.04em]";
const tdPrimaryClass = "px-4 py-3 text-slate-700 dark:text-gray-200";
const tdSecondaryClass = "px-4 py-3 tabular-nums text-slate-500 dark:text-gray-400";

const FIELD_ICONS = {
    id: Hash,
    accountNo: CreditCard,
    projectId: Building2,
    civilId: FileText,
    phoneNo: Phone,
    createdBy: User,
    createdByName: User,
    createdDate: CalendarDays,
    osAtCreation: Wallet,
    advanceAmount: Banknote,
    advancePaymentDate: CalendarDays,
    firstInstalmentDate: CalendarClock,
    noOfInstalments: ListOrdered,
    remarks: FileText,
    attachment: Paperclip,
};
const getFieldIcon = (key) => FIELD_ICONS[key] || FileText;


export const InactivePlanDetailModal = ({ plan, onClose, formatDate, langMode, translations }) => {
    if (!plan) return null;

    const mappedPlan = {
        id: plan.planId,
        osAtCreation: plan.outstandingAtCreation,
        ...plan
    };

    const displayKeys = [
        "id",
        "accountNo",
        "projectId",
        "civilId",
        "phoneNo",
        "createdBy",
        "createdByName",
        "createdDate",
        "isActive",
        "osAtCreation",
        "advanceAmount",
        "advancePaymentDate",
        "firstInstalmentDate",
        "noOfInstalments",
        "remarks",
        "attachment",
    ];

    const handleViewAttachment = (fileUrl) => {
        if (!fileUrl) return;
        const isPDF = fileUrl.toLowerCase().endsWith(".pdf");

        if (isPDF) {
            window.open(fileUrl, "_blank", "noopener,noreferrer");
        } else {
            const newTab = window.open("", "_blank");
            if (newTab) {
                newTab.document.write(
                    `<img src="${fileUrl}" style="width:100%;height:auto;" />`
                );
            }
        }
    };

    const formatValue = (key, value) => {
        if (value === null || value === undefined) {
            return 'N/A';
        }

        switch (key) {
            case 'createdDate':
            case 'advancePaymentDate':
            case 'firstInstalmentDate':
                return typeof formatDate === 'function' ? formatDate(value) : String(value);
            case 'isActive':
                return value ? 'Yes' : 'No';
            case 'advanceAmount':
            case 'osAtCreation':
                return `OMR ${Number(value).toFixed(2)}`;
            case 'phoneNo':
                return String(value).trim();
            default:
                return String(value);
        }
    };

    const isWideField = (key) => key === 'remarks' || key === 'attachment';

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 flex justify-center items-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl transform transition-all">
                <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-white">
                        Inactive Plan Details
                    </h3>
                    <ModalCloseButton onClick={onClose} ariaLabel="Close" />
                </div>
                <div className="max-h-[70vh] overflow-y-auto p-5">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {displayKeys.map((key) => {
                            const value = mappedPlan[key];
                            const label = formatKeyAsLabel(key);
                            const wide = isWideField(key);
                            const Icon = getFieldIcon(key);

                            if (key === 'attachment') {
                                return (
                                    <div key={key} className={detailCardClass(wide)}>
                                        <span className={detailIconChipClass}><Icon size={14} /></span>
                                        <div className="min-w-0 flex-1">
                                            <p className={detailLabelClass}>{label}</p>
                                            {value ? (
                                                <span
                                                    onClick={() => handleViewAttachment(value)}
                                                    className="inline-block cursor-pointer rounded-md bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-200 transition-colors"
                                                >
                                                    {translations?.[langMode]?.viewattachment || 'View Attachment'}
                                                </span>
                                            ) : (
                                                <p className={detailValueClass}>N/A</p>
                                            )}
                                        </div>
                                    </div>
                                );
                            }

                            if (key === 'isActive') {
                                const StatusIcon = value ? CheckCircle2 : XCircle;
                                return (
                                    <div key={key} className={detailCardClass(wide)}>
                                        <span className={detailIconChipClass}><StatusIcon size={14} /></span>
                                        <div className="min-w-0">
                                            <p className={detailLabelClass}>{label}</p>
                                            <p className={`text-sm font-bold ${value ? "text-green-600" : "text-red-600"}`}>
                                                {formatValue(key, value)}
                                            </p>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div key={key} className={detailCardClass(wide)}>
                                    <span className={detailIconChipClass}><Icon size={14} /></span>
                                    <div className="min-w-0">
                                        <p className={detailLabelClass}>{label}</p>
                                        <p className={detailValueClass}>{formatValue(key, value)}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {mappedPlan.details && mappedPlan.details.length > 0 && (
                        <div className="mt-5">
                            <h4 className="text-sm font-semibold mb-3 text-gray-800 dark:text-white">
                                {translations?.[langMode]?.instalmentDetails || 'Instalment Details'}
                            </h4>
                            <div className={tableCardClass}>
                                <table className="min-w-full table-auto whitespace-nowrap text-xs">
                                    <thead className={tableHeadClass}>
                                        <tr>
                                            <th className={thClass}>{translations?.[langMode]?.No}.</th>
                                            <th className={thClass}>{translations?.[langMode]?.PayAmt}</th>
                                            <th className={thClass}>{translations?.[langMode]?.payDate}</th>
                                            <th className={thClass}>{translations?.[langMode]?.Status}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mappedPlan.details.map((detail, index) => (
                                            <tr key={detail.detailId || index} className={tableRowClass}>
                                                <td className={tdSecondaryClass}>{index + 1}</td>
                                                <td className={tdPrimaryClass}>{`OMR ${Number(detail.paymentAmount).toFixed(2)}`}</td>
                                                <td className={tdSecondaryClass}>
                                                    {typeof formatDate === 'function' ? formatDate(detail.paymentDate) : detail.paymentDate}
                                                </td>
                                                <td className={`px-4 py-3 font-semibold ${detail.isPaid ? 'text-green-600' : 'text-red-600'}`}>
                                                    {detail.isPaid ? (translations?.[langMode]?.paid || 'Paid') : (translations?.[langMode]?.unpaid || 'Unpaid')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
